import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, MessageCircle, Users, Building2, Settings, LogOut, Menu, X, LifeBuoy, Shield, BookOpen, Handshake, CalendarDays, Scale } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useNotifications } from "@/hooks/useNotifications";

const navItems = [
  { to: "/app", label: "Painel", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/app/chat", label: "WhatsApp", icon: MessageCircle, testid: "nav-chat" },
  { to: "/app/leads", label: "Leads", icon: Users, testid: "nav-leads" },
  { to: "/app/agenda", label: "Agenda", icon: CalendarDays, testid: "nav-agenda" },
  { to: "/app/imoveis", label: "Imóveis", icon: Building2, testid: "nav-properties" },
  { to: "/app/agenciamento", label: "Agenciamento", icon: Handshake, testid: "nav-agenciamento" },
  { to: "/app/juridico", label: "Jurídico", icon: Scale, testid: "nav-juridico" },
  { to: "/app/config", label: "Configurações", icon: Settings, testid: "nav-settings" },
  { to: "/app/onboarding", label: "Guia rápido", icon: BookOpen, testid: "nav-onboarding" },
  { to: "/app/suporte", label: "Suporte", icon: LifeBuoy, testid: "nav-support" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  useNotifications();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-[#FAFAF8]">
      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 bg-white/90 backdrop-blur border-b border-stone-200 px-4 h-14 flex items-center justify-between">
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-2 -ml-2">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <Link to="/app" className="font-heading font-bold text-lg text-[#1B3A2F]">estatebroker.AI</Link>
        <div className="w-9 h-9 rounded-full bg-[#1B3A2F] text-white flex items-center justify-center font-semibold text-sm" data-testid="user-avatar">
          {user?.name?.[0]?.toUpperCase() || "C"}
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky top-0 left-0 z-30 h-screen w-72 bg-white border-r border-stone-200 
        transform transition-transform lg:transform-none
        ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col
      `}>
        <div className="px-6 pt-6 pb-4 border-b border-stone-100">
          <Link to="/app" onClick={() => setOpen(false)} className="block">
            <div className="font-heading text-2xl font-extrabold text-[#1B3A2F] tracking-tight">
              estatebroker<span className="text-[#3B9EFF]">.AI</span>
            </div>
            <div className="text-xs text-stone-500 mt-0.5">Sua IA que te ajuda a vender</div>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(({ to, label, icon: Icon, testid, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setOpen(false)}
              data-testid={testid}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm transition
                ${isActive
                  ? 'bg-[#1B3A2F] text-white'
                  : 'text-stone-700 hover:bg-stone-100'}
              `}
            >
              <Icon className="w-5 h-5" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-stone-100">
          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="go-to-admin-link"
              className="flex items-center gap-2 mb-3 text-xs font-semibold text-[#D4651A] hover:text-[#A04E10] bg-[#D4651A]/10 rounded-lg px-3 py-2"
            >
              <Shield className="w-3.5 h-3.5" /> Ir para painel admin
            </Link>
          )}
          <div className="flex items-center gap-3 px-2 py-2 mb-2">
            <div className="w-10 h-10 rounded-full bg-[#1B3A2F] text-white flex items-center justify-center font-bold">
              {user?.name?.[0]?.toUpperCase() || "C"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-xs text-stone-500 truncate">Plano {user?.plan}</div>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={logout}
            data-testid="logout-btn"
            className="w-full justify-start text-stone-600 hover:text-red-600"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>
      </aside>

      {/* Overlay */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-20 bg-black/30" onClick={() => setOpen(false)} />
      )}

      {/* Main content */}
      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
